<template>
  <div class="excel-grid-wrapper" @keydown="onGridKeydown" @blur="onGridBlur" tabindex="0" ref="gridContainer">

    <!-- Help bar: cancel + help + layout + column selector buttons -->
    <div class="excel-grid-help-bar">
      <!-- Slot para ações customizadas do consumidor (recebe a linha selecionada) -->
      <slot name="toolbar-actions"
            :selected-row="selectedRow"
            :selected-row-index="selectedCell?.rowIndex ?? null" />
      <button v-if="!readonly && hasAnyChanges()" type="button" class="excel-grid-cancel-btn"
              @click="onCancelAll" title="Cancelar todas as alterações">
        <i class="bi bi-x-lg"></i>
      </button>
      <button v-if="allowDeleteRows && !readonly" type="button" class="excel-grid-delete-btn"
              :disabled="!selectedCell"
              @click="onDeleteSelectedRow"
              :title="selectedCell ? 'Excluir linha selecionada' : 'Selecione uma célula para excluir a linha'">
        <i class="bi bi-trash"></i>
      </button>
      <button v-if="moduloLayout" type="button" class="excel-grid-layout-btn"
              @click="abrirGerenciadorLayout" title="Gerenciar layouts">
        <i class="bi bi-layout-three-columns"></i>
      </button>
      <button v-if="permitirSelecaoColunas" type="button" class="excel-grid-gear-btn"
              @click="showColumnPopover = !showColumnPopover" title="Selecionar colunas">
        <i class="bi bi-gear"></i>
      </button>
      <button v-if="helpEnabled" type="button" class="excel-grid-help-btn"
              @click="showHelp = !showHelp" title="Como usar o grid Excel">
        <i class="bi bi-question-lg"></i>
      </button>
    </div>

    <!-- Modal de Gerenciamento de Layouts -->
    <GerenciadorLayoutModal
      v-if="moduloLayout"
      :model-value="isLayoutModalOpen"
      :modulo="moduloLayout"
      :colunas-disponiveis="colunasPadrao || columns"
      :layout-atual="layoutAtualSnapshot"
      @update:model-value="isLayoutModalOpen = $event"
      @aplicar-layout="aplicarLayoutExterno"
      @show-notification="emit('show-notification', $event)"
    />

    <!-- Help tooltip -->
    <div v-if="showHelp" class="excel-grid-help-panel" @click.stop>
      <div class="excel-grid-help-header">
        <strong><i class="bi bi-keyboard me-1"></i> Atalhos do Grid</strong>
        <button type="button" class="btn-close btn-close-white" @click="showHelp = false"></button>
      </div>
      <div class="excel-grid-help-body">
        <table class="excel-grid-help-table">
          <tbody>
          <tr>
            <td class="excel-grid-help-key"><kbd>↑</kbd> <kbd>↓</kbd> <kbd>←</kbd> <kbd>→</kbd></td>
            <td>Navegar entre células</td>
          </tr>
          <tr>
            <td class="excel-grid-help-key"><kbd>F2</kbd> ou <kbd>Enter</kbd></td>
            <td>Editar célula selecionada</td>
          </tr>
          <tr>
            <td class="excel-grid-help-key"><kbd>Digitar</kbd></td>
            <td>Substitui o conteúdo e entra em edição</td>
          </tr>
          <tr>
            <td class="excel-grid-help-key"><kbd>Enter</kbd> (editando)</td>
            <td>Salva e permanece na célula</td>
          </tr>
          <tr>
            <td class="excel-grid-help-key"><kbd>Tab</kbd> (editando)</td>
            <td>Salva e vai para a direita</td>
          </tr>
          <tr>
            <td class="excel-grid-help-key"><kbd>Esc</kbd> (editando)</td>
            <td>Cancela e volta ao valor original</td>
          </tr>
          </tbody>
        </table>
        <hr class="my-2" />
        <div class="excel-grid-help-legend">
          <div><span class="excel-grid-help-swatch excel-grid-help-swatch-selected"></span> Célula selecionada</div>
          <div><span class="excel-grid-help-swatch excel-grid-help-swatch-editing"></span> Célula em edição</div>
          <div><span class="excel-grid-help-swatch excel-grid-help-swatch-required"></span> Campo obrigatório</div>
          <div><span class="excel-grid-help-swatch excel-grid-help-swatch-readonly"></span> Campo não editável (calculado ou preenchido automaticamente)</div>
        </div>
        <hr class="my-2" />
        <div class="excel-grid-help-note">
          <i class="bi bi-info-circle me-1"></i>
          Apenas <strong>1 linha não salva</strong> por vez. Preencha ou saia dela para adicionar outra.
        </div>
        <div class="excel-grid-help-note">
          <i class="bi bi-check2-circle me-1"></i>
          O salvamento é <strong>automático</strong>: ao completar os campos obrigatórios ou concluir a edição de uma célula, a linha é salva.
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="excel-grid-loading">
      <div class="spinner-border spinner-border-sm text-warning me-2"></div>
      Carregando...
    </div>

    <!-- Grid table (sempre renderizada, mesmo vazia) -->
    <div
      v-if="!loading"
      ref="tableWrapRef"
      class="excel-grid-table-wrap"
      :class="{ 'table-overflows-x': tableOverflowsX }"
    >
      <table class="table table-sm excel-grid-table m-0">
        <thead>
          <tr>
            <th
              v-for="col in colunasRenderizadas"
              :key="col.field"
              :style="thStyle(col)"
              :class="colAlignClass(col)"
            >
              {{ col.label }}
              <span v-if="col.required" class="excel-grid-required-mark" title="Campo obrigatório">*</span>
              <div
                v-if="permitirResize && col.resizable !== false"
                class="resize-handle"
                @mousedown.prevent="onResizeStart($event, col.field)"
              ></div>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Empty state message (quando vazio e sem permissao de adicionar) -->
          <tr v-if="data.length === 0 && (!allowAddRows || readonly)">
            <td :colspan="colunasRenderizadas.length" class="text-center py-4 text-muted">
              <slot name="empty">
                <i class="bi bi-info-circle me-2"></i> {{ emptyMessage || 'Nenhum registro encontrado.' }}
              </slot>
            </td>
          </tr>
          <!-- Data rows -->
          <tr
            v-for="(row, rowIndex) in data"
            :key="row[rowKey] || row._tempId || rowIndex"
            :class="[
              rowClass ? rowClass(row) : '',
              {
                'excel-grid-row-selected': selectedCell?.rowIndex === rowIndex,
                'row-pending-save': row._pendingSave
              }
            ]"
          >
            <td
              v-for="(col, colIndex) in colunasRenderizadas"
              :key="col.field"
              :style="thStyle(col)"
              :class="[
                'excel-grid-cell',
                colAlignClass(col),
                {
                  'excel-grid-cell-selected': selectedCell?.rowIndex === rowIndex && selectedCell?.colIndex === colIndex && !editingCell,
                  'excel-grid-cell-editing': editingCell?.rowIndex === rowIndex && editingCell?.colIndex === colIndex,
                  'excel-grid-cell-readonly': col.type !== 'actions' && !isCellEditable(col, row),
                  'excel-grid-cell-actions': col.type === 'actions',
                  'excel-grid-cell-has-content': col.type !== 'actions' && !isCellDisplayEmpty(row, col),
                  'excel-grid-cell-required': isCellRequired(col, row) && !editingCell && !isEditing(rowIndex, colIndex) && isCellValueEmpty(row, col)
                }
              ]"
              @click="onCellClick(rowIndex, colIndex)"
              @dblclick="onCellDblClick(row, rowIndex, col, colIndex)"
            >
              <template v-if="col.type === 'text'">
                <template v-if="isEditing(rowIndex, colIndex)">
                  <input ref="cellInput" v-model="editValue" type="text" class="excel-grid-input"
                    :disabled="!isCellEditable(col, row)"
                    @keydown="onInputKeydown($event, row, rowIndex, col, colIndex)"
                    @blur="confirmEdit(row, rowIndex, col, colIndex)" @click.stop />
                </template>
                <template v-else>
                  <span class="excel-grid-cell-text">{{ row[col.field] }}</span>
                </template>
              </template>
              <template v-if="col.type === 'date'">
                <template v-if="isEditing(rowIndex, colIndex)">
                  <div class="excel-grid-date-editor">
                    <input ref="cellInput" :value="editValue" type="text" inputmode="numeric"
                      class="excel-grid-input" placeholder="dd/mm/aaaa" maxlength="10"
                      :disabled="!isCellEditable(col, row)"
                      @input="onDateTextInput($event)"
                      @keydown="onInputKeydown($event, row, rowIndex, col, colIndex)"
                      @blur="confirmEdit(row, rowIndex, col, colIndex)" @click.stop />
                    <button type="button" class="excel-grid-date-btn" title="Selecionar data no calendário"
                      :disabled="!isCellEditable(col, row)"
                      @mousedown.prevent="abrirCalendarioData()">
                      <i class="bi bi-calendar3"></i>
                    </button>
                    <input ref="nativeDateInput" type="date" class="excel-grid-date-native"
                      :value="editValueISO" tabindex="-1" aria-hidden="true"
                      @change="onCalendarioDataSelecionada($event, row, rowIndex, col, colIndex)" />
                  </div>
                </template>
                <template v-else>
                  <div class="excel-grid-cell-inner">
                    <span class="excel-grid-cell-text">{{ formatDateDisplay(row[col.field]) }}</span>
                    <button v-if="!readonly && isCellEditable(col, row)" type="button"
                      class="excel-grid-lookup-btn"
                      @click.stop="abrirDatePickerDaCelula(row, rowIndex, col, colIndex)"
                      title="Selecionar data no calendário">
                      <i class="bi bi-calendar3"></i>
                    </button>
                  </div>
                </template>
              </template>
              <template v-if="col.type === 'number'">
                <template v-if="isEditing(rowIndex, colIndex)">
                  <input ref="cellInput" v-model="editValue" type="text" inputmode="decimal"
                    class="excel-grid-input font-mono text-right"
                    :disabled="!isCellEditable(col, row)"
                    @keydown="onInputKeydown($event, row, rowIndex, col, colIndex)"
                    @blur="confirmEdit(row, rowIndex, col, colIndex)" @click.stop />
                </template>
                <template v-else>
                  <span class="excel-grid-cell-text font-mono">{{ formatNumberDisplay(row[col.field], col.decimals, col.decimalsMinimo) }}</span>
                </template>
              </template>
              <template v-if="col.type === 'select'">
                <template v-if="isEditing(rowIndex, colIndex)">
                  <select ref="cellInput" v-model="editValue" class="excel-grid-input excel-grid-select"
                    :disabled="!isCellEditable(col, row)"
                    @keydown="onInputKeydown($event, row, rowIndex, col, colIndex)"
                    @blur="confirmEdit(row, rowIndex, col, colIndex)" @click.stop>
                    <option value="" disabled>Selecione...</option>
                    <option v-for="opt in resolveOptions(col, row)"
                      :key="opt.idListaClienteItem || opt.value || opt.idLocalEstoque"
                      :value="opt.idListaClienteItem || opt.value || opt.idLocalEstoque">
                      {{ opt.textoLista || opt.label || opt.text || opt.descricao }}
                    </option>
                  </select>
                </template>
                <template v-else>
                  <span class="excel-grid-cell-text">{{ resolveOptionLabel(row[col.field], col, row) }}</span>
                </template>
              </template>
              <template v-if="col.type === 'badge'">
                <span v-if="col.badgeClassResolver || col.badgeTextResolver"
                  :class="col.badgeClassResolver ? col.badgeClassResolver(row) : ''">
                  {{ col.badgeTextResolver ? col.badgeTextResolver(row) : row[col.field] }}
                </span>
                <span v-else-if="col.badgeMap && col.badgeMap[row[col.field]]"
                  :class="col.badgeMap[row[col.field]].class">
                  {{ col.badgeMap[row[col.field]].text }}
                </span>
                <span v-else class="text-muted small">{{ row[col.field] }}</span>
              </template>
              <template v-if="col.type === 'lookup'">
                <template v-if="isEditing(rowIndex, colIndex)">
                  <input ref="cellInput" v-model="editValue" type="text" class="excel-grid-input"
                    :disabled="!isCellEditable(col, row)"
                    @keydown="onInputKeydown($event, row, rowIndex, col, colIndex)"
                    @blur="confirmEdit(row, rowIndex, col, colIndex)" @click.stop readonly />
                </template>
                <template v-else>
                  <div class="excel-grid-cell-inner">
                    <span class="excel-grid-cell-text">{{ row[col.lookupConfig?.descField || col.field] }}</span>
                    <button v-if="isCellEditable(col, row)" type="button"
                      class="excel-grid-lookup-btn"
                      @click.stop="emitCellLookup(row, rowIndex, col, colIndex)"
                      title="Buscar">
                      <i class="bi bi-search"></i>
                    </button>
                  </div>
                </template>
              </template>
              <template v-if="col.type === 'autocomplete'">
                <template v-if="isEditing(rowIndex, colIndex)">
                  <input ref="cellInput" v-model="editValue" type="text" class="excel-grid-input"
                    :disabled="!isCellEditable(col, row)"
                    @input="onAutocompleteInput($event, row, rowIndex, col, colIndex)"
                    @keydown="onAutocompleteKeydown($event, row, rowIndex, col, colIndex)"
                    @blur="onAutocompleteBlur(row, rowIndex, col, colIndex)" @click.stop />
                </template>
                <template v-else>
                  <div class="excel-grid-cell-inner">
                    <span v-if="resolveBlockBadge(col, row)" :class="resolveBlockBadge(col, row).class">
                      {{ resolveBlockBadge(col, row).text }}
                    </span>
                    <span v-else class="excel-grid-cell-text">{{ row[col.lookupConfig?.descField || col.field] }}</span>
                    <button v-if="isCellEditable(col, row)" type="button"
                      class="excel-grid-lookup-btn"
                      @click.stop="emitCellLookup(row, rowIndex, col, colIndex)"
                      title="Buscar">
                      <i class="bi bi-search"></i>
                    </button>
                  </div>
                </template>
              </template>
              <template v-if="col.type === 'actions'">
                <div class="excel-grid-cell-actions-inner">
                  <slot :name="'cell-' + col.field" :row="row" :row-index="rowIndex">
                    <span class="text-muted small">—</span>
                  </slot>
                </div>
              </template>
            </td>
          </tr>
          <!-- Ghost row for adding new rows (visível apenas quando é permitido adicionar) -->
          <tr v-if="allowAddRows && !readonly && !hasUnpersistedNewRow()" class="excel-grid-ghost-row">
            <td v-for="(col, colIndex) in colunasRenderizadas" :key="col.field"
              :style="thStyle(col)"
              :class="['excel-grid-cell', 'excel-grid-ghost-cell', colAlignClass(col), { 'excel-grid-cell-actions': col.type === 'actions' }]"
              @click="onGhostRowClick(colIndex)"
              :title="col.type === 'actions' ? '' : 'Clique para adicionar nova linha'">
              <template v-if="colIndex === 0">
                <span class="excel-grid-ghost-icon">+</span>
                <span class="excel-grid-ghost-text">Nova linha</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Dropdown de autocomplete (posicionado via fixed para não ser cortado pelo overflow da tabela) -->
    <div v-if="autocompleteState.visible" ref="autocompleteDropdownRef" class="excel-grid-autocomplete-dropdown" :style="autocompleteDropdownStyle">
      <div
        v-for="(item, idx) in autocompleteState.results"
        :key="idx"
        class="excel-grid-autocomplete-item"
        :class="{ 'is-hover': idx === autocompleteState.selectedIndex, 'is-disabled': isAutocompleteItemDisabled(item) }"
        @mousedown.prevent="selecionarAutocompleteItem(item)"
        @mouseenter="autocompleteState.selectedIndex = idx"
      >
        <div class="excel-grid-autocomplete-primary">
          <i class="bi bi-dot me-1"></i>
          <span class="fw-medium">{{ item[autocompletePrimaryField] }}</span>
        </div>
        <div v-if="autocompleteSecondaryFields.length > 0" class="excel-grid-autocomplete-secondary">
          <span v-for="f in autocompleteSecondaryFields" :key="f" class="excel-grid-autocomplete-tag">
            {{ item[f] }}
          </span>
        </div>
      </div>
      <div v-if="autocompleteState.loading" class="excel-grid-autocomplete-empty">
        <span class="spinner-border spinner-border-sm me-1"></span> Buscando...
      </div>
      <div v-else-if="autocompleteState.results.length === 0" class="excel-grid-autocomplete-empty">
        <i class="bi bi-search me-1"></i> Nenhuma opção correspondente.
      </div>
    </div>

    <!-- Column selection popover -->
    <div v-if="permitirSelecaoColunas" class="excel-grid-column-selector">
      <button type="button" class="excel-grid-gear-btn" @click="showColumnPopover = !showColumnPopover" title="Selecionar colunas">
        <i class="bi bi-gear"></i>
      </button>

      <div v-if="showColumnPopover" class="excel-grid-column-popover" @click.stop>
        <div class="excel-grid-column-popover-header">
          <strong>Colunas</strong>
          <button type="button" class="btn-close btn-close-white" @click="showColumnPopover = false"></button>
        </div>
        <div class="excel-grid-column-popover-body">
          <!-- Filtros de busca -->
          <div class="excel-grid-column-search">
            <input v-model="columnSearch" type="text" class="form-control form-control-sm" placeholder="Filtrar colunas..." />
          </div>

          <!-- Dual-pane: Disponíveis / Selecionados -->
          <div class="excel-grid-column-dualpane">
            <!-- Disponíveis -->
            <div class="excel-grid-column-pane">
              <div class="excel-grid-column-pane-header">
                <span>Disponíveis</span>
                <button type="button" class="excel-grid-column-link-btn" @click="selecionarTudo" title="Selecionar todas">
                  <i class="bi bi-arrow-right"></i>
                </button>
              </div>
              <div
                v-for="col in colunasDisponiveisFiltradas"
                :key="col.field"
                class="excel-grid-column-item"
                :class="{ 'excel-grid-column-item-fixed': colunasFixas.includes(col.field) }"
                draggable="true"
                @dragstart="onDragStartDisponivel(col.field)"
                @dragover.prevent="onDragOverDisponivel(col.field)"
                @drop="onDropDisponivel(col.field)"
                @dragend="onDragEnd"
              >
                <span class="excel-grid-column-item-label">{{ col.label }}</span>
                <span v-if="colunasFixas.includes(col.field)" class="excel-grid-column-item-lock" title="Coluna fixa">
                  <i class="bi bi-lock-fill"></i>
                </span>
                <div v-if="!colunasFixas.includes(col.field)" class="excel-grid-column-item-arrows">
                  <button type="button" class="excel-grid-column-arrow-btn" @click="moverColunaDisponivel(col.field, -1)" title="Mover para cima">
                    <i class="bi bi-chevron-up"></i>
                  </button>
                  <button type="button" class="excel-grid-column-arrow-btn" @click="moverColunaDisponivel(col.field, 1)" title="Mover para baixo">
                    <i class="bi bi-chevron-down"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Separador -->
            <div class="excel-grid-column-separator">
              <button type="button" class="excel-grid-column-link-btn" @click="toggleColuna(col.field)" title="Remover">
                <i class="bi bi-arrow-left"></i>
              </button>
            </div>

            <!-- Selecionados -->
            <div class="excel-grid-column-pane">
              <div class="excel-grid-column-pane-header">
                <span>Selecionadas</span>
                <button type="button" class="excel-grid-column-link-btn" @click="limparSelecao" title="Limpar seleção">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
              <div
                v-for="col in colunasSelecionadasFiltradas"
                :key="col.field"
                class="excel-grid-column-item"
                :class="{ 'excel-grid-column-item-fixed': colunasFixas.includes(col.field) }"
                draggable="true"
                @dragstart="onDragStartSelecionado(col.field)"
                @dragover.prevent="onDragOverSelecionado(col.field)"
                @drop="onDropSelecionado(col.field)"
                @dragend="onDragEnd"
              >
                <span class="excel-grid-column-item-label">{{ col.label }}</span>
                <span v-if="colunasFixas.includes(col.field)" class="excel-grid-column-item-lock" title="Coluna fixa">
                  <i class="bi bi-lock-fill"></i>
                </span>
                <div v-if="!colunasFixas.includes(col.field)" class="excel-grid-column-item-arrows">
                  <button type="button" class="excel-grid-column-arrow-btn" @click="moverColunaSelecionado(col.field, -1)" title="Mover para cima">
                    <i class="bi bi-chevron-up"></i>
                  </button>
                  <button type="button" class="excel-grid-column-arrow-btn" @click="moverColunaSelecionado(col.field, 1)" title="Mover para baixo">
                    <i class="bi bi-chevron-down"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="excel-grid-column-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="restaurarLayoutPadrao">Padrão</button>
            <button type="button" class="btn btn-sm btn-primary" @click="fecharColumnPopover">OK</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, computed, onMounted, onUnmounted } from 'vue'
import { formatarDataBR } from '@/utils/useDataBR'
import { layoutService } from '@/services/layoutService'
import GerenciadorLayoutModal from './GerenciadorLayoutModal.vue'

const props = defineProps({
  columns: {
    type: Array,
    required: true
  },
  data: {
    type: Array,
    default: () => []
  },
  allowAddRows: {
    type: Boolean,
    default: false
  },
  allowDeleteRows: {
    type: Boolean,
    default: false
  },
  allowSaveRows: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  rowKey: {
    type: String,
    default: 'id'
  },
  emptyMessage: {
    type: String,
    default: ''
  },
  rowClass: {
    type: Function,
    default: null
  },
  listas: {
    type: Object,
    default: () => ({})
  },
  helpEnabled: {
    type: Boolean,
    default: true
  },
  hasChangesField: {
    type: String,
    default: 'id'
  },
  // Feature flags
  permitirResize: {
    type: Boolean,
    default: true
  },
  moduloLayout: {
    type: String,
    default: null
  },
  permitirSelecaoColunas: {
    type: Boolean,
    default: false
  },
  colunasPadrao: {
    type: Array,
    default: null
  },
  colunasFixas: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'update:data',
  'cell-edit',
  'add-row',
  'delete-row',
  'save-row',
  'cancel-all',
  'show-notification',
  'cell-lookup',
  'cell-autocomplete-select',
  'request-abandon-new-row',
  'layout-change',
  'reset-layout',
  'column-visibility-change'
])

// ============================================================
// STATE
// ============================================================
const selectedCell = ref(null) // { rowIndex, colIndex } — célula selecionada (navegação)
const editingCell = ref(null) // { rowIndex, colIndex, field, originalValue } — célula em edição

// Linha atualmente selecionada (derivada da célula selecionada). Exposta ao
// consumidor via slot `toolbar-actions` para ações que dependem da linha ativa.
const selectedRow = computed(() => {
  const idx = selectedCell.value?.rowIndex
  return idx != null && idx >= 0 ? (props.data[idx] ?? null) : null
})
const editValue = ref('')
const cellInput = ref(null)
const gridContainer = ref(null)
const tableWrapRef = ref(null)
const showHelp = ref(false)

// ============================================================
// Detector de overflow horizontal
// Mantém o thead sticky referenciando a PÁGINA (wrapper sem overflow) no caso
// comum; só ativa .table-overflows-x quando a tabela excede o wrapper.
// ============================================================
const tableOverflowsX = ref(false)
let _overflowObserver = null

const checarOverflowX = () => {
  const wrap = tableWrapRef.value
  if (!wrap) return
  tableOverflowsX.value = wrap.scrollWidth > wrap.clientWidth + 1
}
const newEmptyRows = ref(new Set()) // _tempId de linhas recém-adicionadas sem edição
// Navegação "para cima" interrompida aguardando confirmação de descarte (modal aberto).
// Enquanto preenchido, o grid bloqueia novas adições (estado "em adição").
const pendingAbandon = ref(null)
// Intenção de selecionar a linha recém-adicionada (efetivada no watcher de props.data.length)
const pendingSelectAfterAdd = ref(false)
const pendingColIndex = ref(0)

// ============================================================
// FEATURE: Layout modal
// ============================================================
const isLayoutModalOpen = ref(false)

function abrirGerenciadorLayout () {
  isLayoutModalOpen.value = true
}

// ============================================================
// FEATURE: Resize handlers
// ============================================================
const isResizing = ref(false)
let resizeState = null

function getMinWidthForColumn (field) {
  const colunas = colunasRenderizadas.value
  const col = colunas.find(c => c.field === field)
  return col?.minWidth ?? 60
}

function onResizeStart (event, field) {
  const th = event.target.closest('th')
  if (!th) return

  const startWidth = th.offsetWidth
  const minWidth = getMinWidthForColumn(field)

  resizeState = { field, startX: event.clientX, startWidth, minWidth }
  isResizing.value = true

  document.documentElement.style.userSelect = 'none'
  document.documentElement.style.cursor = 'col-resize'

  document.addEventListener('mousemove', onResizeMove)
  document.addEventListener('mouseup', onResizeEnd)
}

function onResizeMove (event) {
  if (!resizeState) return

  const deltaX = event.clientX - resizeState.startX
  const newWidth = Math.max(resizeState.minWidth, resizeState.startWidth + deltaX)

  columnWidths.value[resizeState.field] = Math.round(newWidth)
}

function onResizeEnd () {
  cleanupResize()
}

function cleanupResize () {
  resizeState = null
  isResizing.value = false

  document.documentElement.style.userSelect = ''
  document.documentElement.style.cursor = ''

  document.removeEventListener('mousemove', onResizeMove)
  document.removeEventListener('mouseup', onResizeEnd)
}

onUnmounted(() => {
  if (resizeState) cleanupResize()
  if (_overflowObserver) {
    _overflowObserver.disconnect()
    _overflowObserver = null
  }
})

// ============================================================
// FEATURE: Column state
// ============================================================
const colunasOcultas = ref(new Set())
const columnWidths = ref({})
const columnOrders = ref({})
const showColumnPopover = ref(false)
const columnSearch = ref('')
const dragSource = ref(null)

// Colunas disponíveis (não ocultas) e selecionadas (visíveis)
const colunasDisponiveis = computed(() => {
  const padrao = props.colunasPadrao || props.columns
  return padrao.filter(col => !colunasOcultas.value.has(col.field))
})

const colunasSelecionadas = computed(() => {
  const padrao = props.colunasPadrao || props.columns
  return padrao.filter(col => colunasOcultas.value.has(col.field))
})

const colunasDisponiveisFiltradas = computed(() => {
  const termo = columnSearch.value.toLowerCase()
  if (!termo) return colunasDisponiveis.value
  return colunasDisponiveis.value.filter(col =>
    col.label.toLowerCase().includes(termo) || col.field.toLowerCase().includes(termo)
  )
})

const colunasSelecionadasFiltradas = computed(() => {
  const termo = columnSearch.value.toLowerCase()
  if (!termo) return colunasSelecionadas.value
  return colunasSelecionadas.value.filter(col =>
    col.label.toLowerCase().includes(termo) || col.field.toLowerCase().includes(termo)
  )
})

// colunasRenderizadas: mantém a coluna 'actions' sempre no fim
const colunasRenderizadas = computed(() => {
  const padrao = props.colunasPadrao || props.columns
  // col.visible === false oculta a coluna sem removê-la do array (mantém a
  // posição estável e evita que o gerenciador de layout a anexe ao fim).
  const visiveis = padrao.filter(col => col.visible !== false && !colunasOcultas.value.has(col.field))

  // Ordena pela ordem salva
  visiveis.sort((a, b) => {
    const orderA = columnOrders.value[a.field] ?? 0
    const orderB = columnOrders.value[b.field] ?? 0
    return orderA - orderB
  })

  // Garante que a coluna 'actions' fique no fim
  const actionsCol = visiveis.find(col => col.type === 'actions')
  if (actionsCol) {
    return visiveis.filter(c => c.type !== 'actions').concat(actionsCol)
  }
  return visiveis
})

// ============================================================
// FEATURE: Layout snapshot
// ============================================================
// Snapshot no formato esperado pelo GerenciadorLayoutModal:
// { colunas: [{ field, label, width, order, visible, sortable, align }], sortField, sortOrder }
const layoutAtualSnapshot = computed(() => {
  const padrao = props.colunasPadrao || props.columns
  const colunas = padrao.map((col, idx) => ({
    field: col.field,
    label: col.label,
    width: columnWidths.value[col.field] ?? col.width ?? null,
    order: columnOrders.value[col.field] ?? idx,
    visible: !colunasOcultas.value.has(col.field),
    sortable: col.sortable !== false,
    align: col.align || 'left'
  }))
  return {
    colunas,
    sortField: '',
    sortOrder: ''
  }
})

function aplicarLayoutExterno (layout) {
  if (!layout) return

  // Aceita tanto { colunas, sortField, sortOrder } (GerenciadorLayoutModal)
  // quanto um array de colunas puro (compatibilidade).
  let colunas = null
  if (Array.isArray(layout)) {
    colunas = layout
  } else if (Array.isArray(layout.colunas)) {
    colunas = layout.colunas
  }
  if (!colunas || colunas.length === 0) return

  const padrao = props.colunasPadrao || props.columns

  // Ordena pela propriedade 'order'
  const ordenadas = [...colunas].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  // Visibilidade: colunas com visible === false ficam ocultas
  const ocultas = new Set()
  for (const item of ordenadas) {
    if (item.visible === false) ocultas.add(item.field)
  }
  colunasOcultas.value = ocultas

  // Ordens e larguras
  const ordens = {}
  const larguras = {}
  ordenadas.forEach((item, idx) => {
    ordens[item.field] = item.order ?? idx
    if (item.width !== null && item.width !== undefined && item.width !== '') {
      larguras[item.field] = item.width
    }
  })

  // Colunas ausentes no layout entram ao fim, preservando visibilidade
  let proxima = ordenadas.length
  for (const col of padrao) {
    if (!(col.field in ordens)) {
      ordens[col.field] = proxima++
    }
  }

  columnOrders.value = ordens
  columnWidths.value = larguras

  emit('layout-change', { layout: layoutAtualSnapshot.value })
}

function restaurarLayoutPadrao () {
  colunasOcultas.value = new Set()
  columnWidths.value = {}
  columnOrders.value = {}
  columnSearch.value = ''
  emit('reset-layout')
  emit('layout-change', { layout: layoutAtualSnapshot.value })
}

async function salvarLayout () {
  if (!props.moduloLayout) return
  try {
    // Atualiza o layout Default do usuário, se existir
    const { data } = await layoutService.getDefault(props.moduloLayout)
    if (data && data.idUserGridLayout) {
      await layoutService.atualizar(data.idUserGridLayout, {
        modulo: props.moduloLayout,
        nome: data.nome,
        descricao: data.descricao || null,
        isDefault: true,
        configuracaoColunas: JSON.stringify(layoutAtualSnapshot.value.colunas),
        sortField: data.sortField || '',
        sortOrder: data.sortOrder || ''
      })
    }
  } catch {
    // Silencioso: falha na persistência de layout não deve quebrar o grid
  }
}

async function carregarLayout () {
  if (!props.moduloLayout) return
  try {
    const { data } = await layoutService.getDefault(props.moduloLayout)
    if (data && data.configuracaoColunas) {
      let colunas = []
      try {
        colunas = JSON.parse(data.configuracaoColunas)
      } catch {
        colunas = []
      }
      if (Array.isArray(colunas) && colunas.length > 0) {
        aplicarLayoutExterno({
          colunas,
          sortField: data.sortField,
          sortOrder: data.sortOrder
        })
      }
    }
  } catch {
    // Silencioso: falha na leitura de layout não deve quebrar o grid
  }
}

// ============================================================
// FEATURE: Column selection methods
// ============================================================
function toggleColuna (field) {
  if (props.colunasFixas.includes(field)) return

  if (colunasOcultas.value.has(field)) {
    colunasOcultas.value.delete(field)
  } else {
    colunasOcultas.value.add(field)
  }
  emit('column-visibility-change', { field, visivel: !colunasOcultas.value.has(field) })
}

// Define a visibilidade de uma coluna programaticamente. Usado por grids que
// controlam colunas dinâmicas conforme o estado das linhas (ex: CUST SIMUL).
function setColumnVisible (field, visible) {
  if (visible) {
    colunasOcultas.value.delete(field)
  } else {
    colunasOcultas.value.add(field)
  }
}

function selecionarTudo () {
  const padrao = props.colunasPadrao || props.columns
  const fixas = new Set(props.colunasFixas)
  for (const col of padrao) {
    if (!fixas.has(col.field)) {
      colunasOcultas.value.delete(col.field)
    }
  }
  emit('column-visibility-change', { field: '*all', visivel: true })
}

function limparSelecao () {
  const padrao = props.colunasPadrao || props.columns
  const fixas = new Set(props.colunasFixas)
  for (const col of padrao) {
    if (!fixas.has(col.field)) {
      colunasOcultas.value.add(col.field)
    }
  }
  emit('column-visibility-change', { field: '*all', visivel: false })
}

function fecharColumnPopover () {
  showColumnPopover.value = false
  salvarLayout()
}

// Drag and drop
function onDragStartDisponivel (field) {
  dragSource.value = { pane: 'disponivel', field }
}

function onDragOverDisponivel (_field) {
  // Apenas visual
}

function onDropDisponivel (targetField) {
  if (!dragSource.value || dragSource.value.pane !== 'disponivel') return
  // Reordena dentro do mesmo pane
  const disponiveis = colunasDisponiveis.value
  const targetIdx = disponiveis.findIndex(c => c.field === targetField)
  const sourceIdx = disponiveis.findIndex(c => c.field === dragSource.value.field)
  if (targetIdx === -1 || sourceIdx === -1 || targetIdx === sourceIdx) return

  const copia = [...disponiveis]
  const [item] = copia.splice(sourceIdx, 1)
  copia.splice(targetIdx, 0, item)

  // Atualiza ordens
  copia.forEach((col, idx) => {
    columnOrders.value[col.field] = idx
  })

  dragSource.value = null
}

function onDragStartSelecionado (field) {
  dragSource.value = { pane: 'selecionado', field }
}

function onDragOverSelecionado (_field) {
  // Apenas visual
}

function onDropSelecionado (targetField) {
  if (!dragSource.value || dragSource.value.pane !== 'selecionado') return
  const selecionadas = colunasSelecionadas.value
  const targetIdx = selecionadas.findIndex(c => c.field === targetField)
  const sourceIdx = selecionadas.findIndex(c => c.field === dragSource.value.field)
  if (targetIdx === -1 || sourceIdx === -1 || targetIdx === sourceIdx) return

  const copia = [...selecionadas]
  const [item] = copia.splice(sourceIdx, 1)
  copia.splice(targetIdx, 0, item)

  // Atualiza ordens
  copia.forEach((col, idx) => {
    columnOrders.value[col.field] = idx
  })

  dragSource.value = null
}

function onDragEnd () {
  dragSource.value = null
}

function moverColunaDisponivel (field, direcao) {
  const disponiveis = colunasDisponiveis.value
  const idx = disponiveis.findIndex(c => c.field === field)
  const novoIdx = idx + direcao
  if (novoIdx < 0 || novoIdx >= disponiveis.length) return

  const copia = [...disponiveis]
  const [item] = copia.splice(idx, 1)
  copia.splice(novoIdx, 0, item)

  copia.forEach((col, i) => {
    columnOrders.value[col.field] = i
  })
}

function moverColunaSelecionado (field, direcao) {
  const selecionadas = colunasSelecionadas.value
  const idx = selecionadas.findIndex(c => c.field === field)
  const novoIdx = idx + direcao
  if (novoIdx < 0 || novoIdx >= selecionadas.length) return

  const copia = [...selecionadas]
  const [item] = copia.splice(idx, 1)
  copia.splice(novoIdx, 0, item)

  copia.forEach((col, i) => {
    columnOrders.value[col.field] = i
  })
}

// ============================================================
// WATCH: detect new rows added and track them
// ============================================================
watch(() => (Array.isArray(props.data) ? props.data.length : 0), (newLen, oldLen) => {
  if (newLen > oldLen) {
    // Nova linha foi adicionada — identifica qual é
    for (let i = newLen - 1; i >= oldLen; i--) {
      const row = props.data[i]
      if (row._tempId) {
        newEmptyRows.value.add(row._tempId)
      }
    }
    // Seleção confiável pós-adição: somente aqui a linha já existe de fato
    // no array renderizado, evitando índice defasado no clique em "Nova Linha".
    if (pendingSelectAfterAdd.value) {
      const alvo = newLen - 1
      const colAlvo = pendingColIndex.value
      pendingSelectAfterAdd.value = false
      pendingColIndex.value = 0
      if (alvo >= 0 && alvo < props.data.length && props.data[alvo]) {
        selectCell(alvo, colAlvo)
      }
    }
  }
})

// ============================================================
// FEATURE: Resize com larguras percentuais
// ============================================================
// Converte largura para CSS válido: números viram 'px' (o CSSOM ignora números sem unidade)
function toWidthCss (value) {
  if (value === null || value === undefined || value === '') return null
  return typeof value === 'number' ? value + 'px' : value
}

function thStyle (col) {
  if (!props.permitirResize) {
    const largura = toWidthCss(col.width)
    return largura ? { width: largura } : {}
  }

  // Prioriza columnWidths (px) sobre col.width (ex: '15%')
  const larguraCustom = toWidthCss(columnWidths.value[col.field])
  if (larguraCustom) {
    return { width: larguraCustom, minWidth: '50px' }
  }

  const larguraPadrao = toWidthCss(col.width)
  if (larguraPadrao) {
    return { width: larguraPadrao, minWidth: '50px' }
  }

  return { minWidth: '50px' }
}

// ============================================================
// AUTOCOMPLETE (colunas type: 'autocomplete')
// ============================================================
const autocompleteState = ref({
  rowIndex: null,
  colIndex: null,
  results: [],
  visible: false,
  selectedIndex: -1,
  loading: false,
  rect: null
})
let autocompleteTimer = null

const autocompleteActiveCol = computed(() => {
  const { colIndex } = autocompleteState.value
  const colunas = colunasRenderizadas.value
  return colIndex != null ? colunas[colIndex] : null
})

const autocompletePrimaryField = computed(() => {
  const ac = autocompleteActiveCol.value?.autocompleteConfig || {}
  return ac.displayField || autocompleteActiveCol.value?.field
})

const autocompleteSecondaryFields = computed(() => {
  const ac = autocompleteActiveCol.value?.autocompleteConfig || {}
  return ac.secondaryFields || []
})

const autocompleteDropdownRef = ref(null)

const autocompleteDropdownStyle = computed(() => {
  const r = autocompleteState.value.rect
  if (!r) return {}
  return {
    position: 'fixed',
    top: r.top + 'px',
    left: r.left + 'px',
    minWidth: r.width + 'px',
    maxWidth: '90vw',
    zIndex: 2000
  }
})

// Ajusta a largura do dropdown ao conteúdo real (sem cortar caracteres)
function ajustarLarguraDropdown () {
  const el = autocompleteDropdownRef.value
  if (!el) return
  const rect = autocompleteState.value.rect || {}
  const minW = rect.width || 0
  const larguraMaxima = Math.floor(window.innerWidth * 0.9)

  // Mede a largura NATURAL do conteúdo. O scrollWidth dos filhos é capado ao
  // clientWidth do container, então setamos o próprio dropdown para
  // width: max-content e lemos a largura resultante.
  el.style.width = 'max-content'
  const natural = Math.ceil(el.getBoundingClientRect().width) || 0

  const largura = Math.min(Math.max(natural, minW), larguraMaxima)
  el.style.width = largura + 'px'
  // Reposiciona se estourar a borda direita da viewport
  const left = rect.left || 0
  const maxLeft = window.innerWidth - largura - 8
  el.style.left = Math.max(8, Math.min(left, maxLeft)) + 'px'
}

watch(
  () => [autocompleteState.value.results, autocompleteState.value.visible, autocompleteState.value.loading],
  () => {
    if (autocompleteState.value.visible) {
      nextTick(() => ajustarLarguraDropdown())
    }
  }
)

// ============================================================
// METHODS
// ============================================================
function isEditing (rowIndex, colIndex) {
  return editingCell.value?.rowIndex === rowIndex && editingCell.value?.colIndex === colIndex
}

function isRowPersisted (row) {
  const idField = props.hasChangesField || props.rowKey || 'id'
  return !!row[idField]
}

function resolveOptions (col, row) {
  if (col.resolveOptions && typeof col.resolveOptions === 'function') {
    return col.resolveOptions(row || {})
  }
  if (col.options) return col.options
  if (col.optionsList && props.listas[col.optionsList]) return props.listas[col.optionsList]
  return []
}

function resolveOptionLabel (value, col, row) {
  if (!value && value !== 0) return ''
  const options = resolveOptions(col, row)
  const found = options.find(o =>
    (o.idListaClienteItem || o.value || o.idLocalEstoque) === value
  )
  return found ? (found.textoLista || found.label || found.text || found.descricao) : value
}

function formatDateDisplay (value) {
  if (!value) return ''
  return formatarDataBR(value)
}

function formatNumberDisplay (value, decimals, minDecimals) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (isNaN(num)) return value
  const maxDec = decimals !== undefined && decimals !== null ? decimals : 4
  // Mínimo de casas exibidas (ex.: campos de valores em moeda exigem no mínimo 4).
  const minDec = minDecimals !== undefined && minDecimals !== null ? minDecimals : 0
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: minDec,
    maximumFractionDigits: maxDec
  }).format(num)
}

function parseNumberPtBr (str) {
  if (!str) return null
  let cleaned = str.replace(/\./g, '')
  cleaned = cleaned.replace(',', '.')
  const num = parseFloat(cleaned)
  return isNaN(num) ? null : num
}

// Sentinela de vazio para colunas numéricas. O valor 0 é interpretado como
// "vazio" (ver arquitetura_excel_grid.md §4.8 — "Sentinela de vazio em colunas
// numéricas"). Sustenta o controle de "1 linha em adição" (isRowBlank /
// hasUnpersistedNewRow) e o auto-save (areRequiredCellsFilled).
const EMPTY_NUMBER_SENTINEL = 0

// true quando o valor de uma coluna numérica deve ser tratado como vazio.
function isNumberValueEmpty (value) {
  return value === null || value === undefined || value === '' || Number(value) === EMPTY_NUMBER_SENTINEL
}

function isCellValueEmpty (row, col) {
  if (col.type === 'number') {
    // 0 = sentinela de vazio (EMPTY_NUMBER_SENTINEL) — ver §4.8 da arquitetura.
    return isNumberValueEmpty(row[col.field])
  }
  return row[col.field] === null || row[col.field] === undefined || row[col.field] === ''
}

// Verifica se todos os campos obrigatórios da linha estão preenchidos.
// Usado para o auto-save: ao completar os obrigatórios, a linha é salva.
// Colunas numéricas usam o sentinela 0 = vazio (via isCellValueEmpty), então um
// campo obrigatório numérico só conta como preenchido quando > 0 — ver §4.8.
function areRequiredCellsFilled (row) {
  return props.columns
    .filter(col => isCellRequired(col, row) && col.type !== 'actions')
    .every(col => !isCellValueEmpty(row, col))
}

// Verifica se a coluna é obrigatória para a linha.
// required pode ser booleano (global) ou função (row) => boolean (por linha).
function isCellRequired (col, row) {
  if (typeof col.required === 'function') return col.required(row)
  return !!col.required
}

// Classe de alinhamento da coluna. Números e datas são alinhados à direita
// por padrão, salvo quando a coluna define explicitamente "align".
function colAlignClass (col) {
  if (col.align) return 'text-' + col.align
  if (col.type === 'number' || col.type === 'date') return 'text-right'
  return ''
}

// Verifica se o TEXTO EXIBIDO na célula está vazio.
// Em colunas lookup/autocomplete o valor exibido é o campo de descrição
// (ex: descricaoLocal), e não o campo de ID — que pode estar preenchido
// enquanto a descrição ainda não foi carregada.
function isCellDisplayEmpty (row, col) {
  const displayField = (col.type === 'lookup' || col.type === 'autocomplete')
    ? (col.lookupConfig?.descField || col.field)
    : col.field
  const val = row[displayField]
  return val === null || val === undefined || val === ''
}

// Verifica se uma célula de dados tem conteúdo REAL para o usuário.
// Números 0 (sentinela de vazio de linha nova, EMPTY_NUMBER_SENTINEL — ver §4.8)
// contam como vazios; lookup/autocomplete usam o campo de descrição (texto exibido).
function cellHasContent (row, col) {
  if (col.type === 'number') {
    // 0 = sentinela de vazio — ver §4.8 da arquitetura do ExcelGrid.
    return !isNumberValueEmpty(row[col.field])
  }
  return !isCellDisplayEmpty(row, col)
}

// Linha "em branco": nenhuma coluna de dados (exceto badge/actions) tem conteúdo.
// Usado para decidir se uma linha nova pode ser descartada com segurança.
// Números usam o sentinela 0 = vazio (cellHasContent/isNumberValueEmpty — §4.8),
// por isso novos registros devem iniciar campos numéricos com 0.
function isRowBlank (row) {
  if (!row) return true
  return props.columns
    .filter(col => col.type !== 'actions' && col.type !== 'badge')
    .every(col => !cellHasContent(row, col))
}

function isCellEditable (col, row) {
  if (props.readonly) return false
  // editable pode ser booleano (global) ou função (row) => boolean (por linha)
  if (typeof col.editable === 'function') return col.editable(row)
  if (col.editable === false && col.editable !== undefined) return false
  if (col.type === 'badge' || col.type === 'actions') return false
  return true
}

// Badge de bloqueio para células do tipo autocomplete: quando o resolver
// retorna { text, class }, a célula é renderizada como badge (bloqueada) em
// vez do input de autocomplete. Retorna null quando a célula está liberada.
function resolveBlockBadge (col, row) {
  if (typeof col.blockBadgeResolver !== 'function') return null
  return col.blockBadgeResolver(row) || null
}

function markRowAsEdited (row) {
  if (row._tempId && newEmptyRows.value.has(row._tempId)) {
    newEmptyRows.value.delete(row._tempId)
  }
}

// Emite 'cell-lookup' marcando a linha como editada: a seleção via modal conta
// como preenchimento (evita que a limpeza de linhas novas a remova no blur).
function emitCellLookup (row, rowIndex, col, colIndex) {
  markRowAsEdited(row)
  emit('cell-lookup', { row, rowIndex, col, colIndex })
}

function removeEmptyRows (skipRowIdx) {
  const toRemove = []
  for (let i = 0; i < props.data.length; i++) {
    const row = props.data[i]
    // Só considera linhas novas (com _tempId rastreado) E que ainda NÃO foram
    // persistidas. Linhas já salvas jamais são removidas pela limpeza — apenas
    // por ação explícita do usuário (botão de excluir da toolbar).
    const isEmpty = row._tempId && newEmptyRows.value.has(row._tempId) && !isRowPersisted(row) && isRowBlank(row)
    // Só pula a linha de destino — a linha atual (que está sendo deselecionada) DEVE ser verificada
    const isSkipped = i === skipRowIdx
    if (isEmpty && !isSkipped) {
      toRemove.push({ row, rowIndex: i })
    }
  }
  for (const item of toRemove) {
    newEmptyRows.value.delete(item.row._tempId)
    emit('delete-row', { row: item.row, rowIndex: item.rowIndex, cleanup: true })
  }
}

// Trava síncrona "uma linha em adição por vez": existe linha nova (_tempId) que
// ainda NÃO foi persistida. Baseada em props.data (e não no set assíncrono
// newEmptyRows), evitando que o key-repeat adicione várias linhas e que a trava
// se perca quando a linha ganha conteúdo antes de ser salva.
function hasUnpersistedNewRow () {
  return props.data.some(r => r._tempId && !isRowPersisted(r))
}

// Cancela todas as alterações: limpa o controle interno de linhas novas vazias
// (senão o grid continua considerando que está em adição e bloqueia novas
// linhas) e notifica o pai para remover as linhas não persistidas.
function onCancelAll () {
  newEmptyRows.value.clear()
  emit('cancel-all')
}

// Exclui a linha atualmente selecionada — ação EXPLÍCITA do usuário via
// toolbar. Sempre notifica o pai (que decide se remove no backend quando a
// linha já está persistida) e reposiciona a seleção na linha vizinha.
function onDeleteSelectedRow () {
  if (!selectedCell.value) return
  const rowIndex = selectedCell.value.rowIndex
  const colIndex = selectedCell.value.colIndex
  const row = props.data[rowIndex]
  if (!row) return

  // Higiene: libera o _tempId para não deixar o controle de "em adição" preso.
  if (row._tempId) newEmptyRows.value.delete(row._tempId)
  emit('delete-row', { row, rowIndex, explicit: true })

  nextTick(() => {
    if (props.data.length === 0) {
      selectedCell.value = null
      if (gridContainer.value) gridContainer.value.focus()
      return
    }
    const novoIndex = Math.min(rowIndex, props.data.length - 1)
    selectCell(novoIndex, colIndex)
  })
}

function hasAnyChanges () {
  if (!props.data) return false
  const idField = props.hasChangesField
  return props.data.some(row => {
    // Linha com _tempId e sem o campo de ID do banco = não salva
    if (row._tempId && !row[idField]) return true
    return false
  })
}

// ============================================================
// AUTOCOMPLETE — funções
// ============================================================
function resetAutocomplete () {
  clearTimeout(autocompleteTimer)
  autocompleteState.value.visible = false
  autocompleteState.value.results = []
  autocompleteState.value.selectedIndex = -1
  autocompleteState.value.loading = false
}

// Indica se um item do autocomplete está desabilitado para a linha atual
// (ex.: produto sem saldo no local selecionado). Configurável por coluna via
// autocompleteConfig.disabledItem(item, row).
function isAutocompleteItemDisabled (item) {
  const col = autocompleteActiveCol.value
  const ac = col?.autocompleteConfig || {}
  if (typeof ac.disabledItem !== 'function') return false
  const row = props.data[autocompleteState.value.rowIndex]
  return !!ac.disabledItem(item, row)
}

// Seleciona o primeiro item habilitado da lista (destaque visível). Se todos
// estiverem desabilitados, mantém -1 (nenhum selecionado).
function selecionarPrimeiroItemHabilitado () {
  const results = autocompleteState.value.results
  let idx = 0
  while (idx < results.length && isAutocompleteItemDisabled(results[idx])) idx++
  autocompleteState.value.selectedIndex = idx < results.length ? idx : -1
}

function onAutocompleteInput (event, row, rowIndex, col, colIndex) {
  const el = event.target
  if (el) {
    const r = el.getBoundingClientRect()
    autocompleteState.value.rect = { top: r.bottom + 2, left: r.left, width: r.width }
  }
  autocompleteState.value.rowIndex = rowIndex
  autocompleteState.value.colIndex = colIndex

  const termo = editValue.value.trim()
  const ac = col.autocompleteConfig || {}
  autocompleteState.value.results = []
  autocompleteState.value.selectedIndex = -1

  if (termo.length < (ac.minChars || 2)) {
    autocompleteState.value.visible = false
    clearTimeout(autocompleteTimer)
    return
  }

  clearTimeout(autocompleteTimer)
  // Exibe o dropdown com "Buscando..." já durante o debounce
  autocompleteState.value.loading = true
  autocompleteState.value.visible = true
  autocompleteTimer = setTimeout(async () => {
    if (typeof ac.searchFn !== 'function') {
      autocompleteState.value.loading = false
      autocompleteState.value.visible = false
      return
    }
    try {
      const results = await ac.searchFn(termo, { row, rowIndex, col, colIndex })
      autocompleteState.value.results = results || []
      // Mantém o dropdown visível mesmo sem resultados, para exibir a
      // mensagem "Nenhuma opção correspondente."
      autocompleteState.value.visible = true
      // Destaque visível no primeiro item habilitado (pula itens sem saldo).
      selecionarPrimeiroItemHabilitado()
    } catch {
      autocompleteState.value.results = []
      autocompleteState.value.visible = false
    } finally {
      autocompleteState.value.loading = false
    }
  }, ac.debounceMs || 350)
}

function selecionarAutocompleteItem (item) {
  const { rowIndex, colIndex } = autocompleteState.value
  const row = props.data[rowIndex]
  const col = colunasRenderizadas.value[colIndex]
  if (!row || !col || !item) return
  if (isAutocompleteItemDisabled(item)) {
    emit('show-notification', {
      message: 'Produto sem saldo disponível no local de estoque selecionado.',
      type: 'warning'
    })
    return
  }
  // A linha deixa de ser "vazia" ao receber um produto válido — evita que a
  // limpeza de linhas novas a remova ao perder o foco.
  markRowAsEdited(row)
  const ac = col.autocompleteConfig || {}
  const displayField = ac.displayField || col.field
  row[col.field] = item[displayField]
  if (ac.idField && item[ac.idField] !== undefined) {
    row[ac.idField] = item[ac.idField]
  }
  resetAutocomplete()
  // Encerra a edição sem emitir 'cell-edit' (o pai trata via 'cell-autocomplete-select')
  editingCell.value = null
  editValue.value = ''
  emit('cell-autocomplete-select', { row, rowIndex, col, colIndex, item })
  nextTick(() => {
    if (gridContainer.value) {
      gridContainer.value.focus()
    }
  })
}

function onAutocompleteKeydown (event, row, rowIndex, col, colIndex) {
  // Separação explícita: enquanto o dropdown está aberto, as setas/Enter/Escape
  // controlam a LISTA e não devem borbulhar para a navegação do GRID.
  if (autocompleteState.value.visible) {
    const teclasLista = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape']
    if (teclasLista.includes(event.key)) {
      event.preventDefault()
      event.stopPropagation()
    }
    const results = autocompleteState.value.results
    if (results.length > 0) {
      if (event.key === 'ArrowDown') {
        const len = results.length
        let idx = Math.min(autocompleteState.value.selectedIndex + 1, len - 1)
        // Pula itens desabilitados (ex.: produto sem saldo no local selecionado).
        while (idx < len && isAutocompleteItemDisabled(results[idx])) idx++
        if (idx < len) autocompleteState.value.selectedIndex = idx
        return
      }
      if (event.key === 'ArrowUp') {
        let idx = Math.max(autocompleteState.value.selectedIndex - 1, 0)
        while (idx >= 0 && isAutocompleteItemDisabled(results[idx])) idx--
        if (idx >= 0) autocompleteState.value.selectedIndex = idx
        return
      }
      if (event.key === 'Enter') {
        if (autocompleteState.value.selectedIndex >= 0) {
          selecionarAutocompleteItem(results[autocompleteState.value.selectedIndex])
        } else {
          resetAutocomplete()
        }
        return
      }
      if (event.key === 'Escape') {
        resetAutocomplete()
        return
      }
    } else if (event.key === 'Escape' || event.key === 'Enter') {
      // Dropdown aberto sem resultados: apenas fecha, sem navegar o grid.
      resetAutocomplete()
      return
    }
  }
  onInputKeydown(event, row, rowIndex, col, colIndex)
}

function onAutocompleteBlur (row, rowIndex, col, colIndex) {
  setTimeout(() => {
    autocompleteState.value.visible = false
    autocompleteState.value.selectedIndex = -1
  }, 200)
  confirmEdit(row, rowIndex, col, colIndex)
}

function startEditing (row, rowIndex, col, colIndex, charTyped) {
  if (!isCellEditable(col, row)) {
    const bloqueadoPorConfig = col.editable === false ||
      (typeof col.editable === 'function' && !col.editable(row))
    if (bloqueadoPorConfig) {
      emit('show-notification', {
        message: `O campo "${col.label}" é somente leitura.`,
        type: 'warning'
      })
    }
    return
  }

  // Marca a linha como editada (remove do set de linhas vazias)
  markRowAsEdited(row)

  // Fecha qualquer dropdown de autocomplete anterior
  resetAutocomplete()

  const rawValue = row[col.field]
  let displayValue = rawValue

  if (charTyped !== undefined) {
    // Se veio de digitação, substitui o conteúdo pela tecla digitada.
    // Datas: aplica a máscara dd/mm/aaaa — o 1º caractere já inicia a digitação.
    displayValue = col.type === 'date' ? maskDate(charTyped) : charTyped
  } else if (col.type === 'number') {
    displayValue = rawValue !== null && rawValue !== undefined ? String(rawValue).replace('.', ',') : ''
  } else if (col.type === 'text') {
    displayValue = rawValue !== null && rawValue !== undefined ? String(rawValue) : ''
  } else if (col.type === 'date') {
    displayValue = isoParaMascara(rawValue ? String(rawValue).split('T')[0] : '')
  } else if (col.type === 'autocomplete') {
    displayValue = rawValue !== null && rawValue !== undefined ? String(rawValue) : ''
  } else if (col.type === 'select') {
    displayValue = rawValue !== null && rawValue !== undefined ? rawValue : ''
  }

  editingCell.value = {
    rowIndex,
    colIndex,
    field: col.field,
    originalValue: rawValue
  }
  editValue.value = displayValue

  nextTick(() => {
    // Focus the active input using querySelector for the editing cell
    const inputs = gridContainer.value?.querySelectorAll('.excel-grid-input')
    if (inputs && inputs.length > 0) {
      const targetInput = inputs[inputs.length - 1]
      if (targetInput) {
        targetInput.focus()
        if (charTyped === undefined) {
          // Entrada explícita (F2 / duplo clique / Enter): seleciona todo o
          // conteúdo para o usuário sobrescrever/editar.
          if (col.type === 'text' || col.type === 'number' || col.type === 'autocomplete' || col.type === 'date') {
            targetInput.select()
          }
        } else if (col.type === 'text' || col.type === 'number' || col.type === 'autocomplete' || col.type === 'date') {
          // Entrada por digitação: o caractere digitado já substituiu o conteúdo
          // anterior; posiciona o cursor no fim, SEM selecionar, para que a
          // próxima tecla acrescente em vez de apagar.
          const len = targetInput.value.length
          targetInput.setSelectionRange(len, len)
        }
      }
    }
  })
}

// ============================================================
// DATA — máscara dd/mm/aaaa (digitação) + calendário nativo
// ============================================================
// Aplica a máscara dd/mm/aaaa sobre os dígitos digitados.
function maskDate (value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

// Converte "yyyy-mm-dd" (ou ISO) para a máscara "dd/mm/aaaa".
function isoParaMascara (iso) {
  if (!iso) return ''
  const m = String(iso).split('T')[0].match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
}

// Converte a máscara "dd/mm/aaaa" para ISO "yyyy-mm-dd" (null se inválida).
function mascaraParaISO (mascara) {
  const m = String(mascara || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  const dia = Number(m[1]); const mes = Number(m[2]); const ano = Number(m[3])
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null
  const d = new Date(Date.UTC(ano, mes - 1, dia))
  if (d.getUTCFullYear() !== ano || d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) return null
  return `${m[3]}-${m[2]}-${m[1]}`
}

// Valor ISO atual da edição, usado pelo input de data (calendário nativo).
const editValueISO = computed(() => mascaraParaISO(editValue.value) || '')

function onDateTextInput (event) {
  const masked = maskDate(event.target.value)
  editValue.value = masked
  event.target.value = masked
}

// Abre o calendário nativo ancorado ao input de data oculto da célula.
function abrirCalendarioData () {
  const input = gridContainer.value?.querySelector('.excel-grid-date-native')
  if (!input) return
  try {
    if (typeof input.showPicker === 'function') {
      input.showPicker()
      return
    }
  } catch {
    // Alguns navegadores exigem foco/click; cai no fallback abaixo.
  }
  input.focus()
  input.click()
}

// A partir da célula em repouso: entra em edição e abre o calendário nativo.
function abrirDatePickerDaCelula (row, rowIndex, col, colIndex) {
  startEditing(row, rowIndex, col, colIndex)
  nextTick(() => {
    abrirCalendarioData()
  })
}

function confirmEdit (row, rowIndex, col, colIndex) {
  if (!editingCell.value) return
  if (editingCell.value.rowIndex !== rowIndex || editingCell.value.colIndex !== colIndex) return

  let newValue = editValue.value

  if (col.type === 'number') {
    newValue = parseNumberPtBr(editValue.value)
  } else if (col.type === 'date') {
    newValue = mascaraParaISO(editValue.value)
  } else if (col.type === 'autocomplete' && col.autocompleteConfig?.requireSelection) {
    // Nenhum item válido foi escolhido na busca/menu suspenso (confirmEdit só
    // é chamado quando NÃO houve seleção). O texto digitado à mão é inválido.
    const ac = col.autocompleteConfig
    const digitado = editValue.value
    const valorOriginal = editingCell.value.originalValue
    const idAtual = ac.idField ? row[ac.idField] : null
    const textoMudou = String(digitado ?? '') !== String(valorOriginal ?? '')

    editingCell.value = null
    editValue.value = ''

    // Só limpa/notifica se não há produto válido associado ou se o texto foi
    // alterado (evita apagar um SKU válido ao apenas entrar e sair da edição).
    if (!idAtual || textoMudou) {
      const temTexto = !!(digitado && String(digitado).trim())
      row[col.field] = ''
      if (ac.idField) row[ac.idField] = null
      if (temTexto) {
        emit('show-notification', {
          message: ac.mensagemInvalido
            ? String(ac.mensagemInvalido).replace('{valor}', digitado)
            : `O valor "${digitado}" é inválido e foi removido.`,
          type: 'warning'
        })
      }
    }
    nextTick(() => {
      if (gridContainer.value) {
        gridContainer.value.focus()
      }
    })
    return
  }

  const oldValue = editingCell.value.originalValue

  row[col.field] = newValue

  emit('cell-edit', {
    row,
    field: col.field,
    value: newValue,
    oldValue
  })

  // Auto-save: se a linha nova ficou completa (todos os campos obrigatórios
  // preenchidos), dispara save-row automaticamente — sem botão de salvar.
  if (!isRowPersisted(row) && areRequiredCellsFilled(row)) {
    emit('save-row', { row, rowIndex })
  }

  editingCell.value = null
  editValue.value = ''

  // Retorna o foco para o grid wrapper para continuar navegando por teclado
  nextTick(() => {
    if (gridContainer.value) {
      gridContainer.value.focus()
    }
  })
}

// Aplica a data escolhida no calendário nativo e já marca a célula como salva.
function onCalendarioDataSelecionada (event, row, rowIndex, col, colIndex) {
  const iso = event.target.value
  if (!iso) return
  editValue.value = isoParaMascara(iso)
  confirmEdit(row, rowIndex, col, colIndex)
}

function cancelEdit () {
  if (!editingCell.value) return
  const { rowIndex, field, originalValue } = editingCell.value
  const row = props.data[rowIndex]
  if (row) {
    row[field] = originalValue
  }
  editingCell.value = null
  editValue.value = ''
  resetAutocomplete()

  // Retorna o foco para o grid wrapper
  nextTick(() => {
    if (gridContainer.value) {
      gridContainer.value.focus()
    }
  })
}

function selectCell (rowIndex, colIndex) {
  if (rowIndex < 0 || rowIndex >= props.data.length) return
  if (colIndex < 0 || colIndex >= colunasRenderizadas.value.length) return

  // Emite save-row ao sair de uma linha nova (para persistencia)
  if (selectedCell.value && selectedCell.value.rowIndex !== rowIndex) {
    const oldRowIndex = selectedCell.value.rowIndex
    const oldRow = props.data[oldRowIndex]
    if (oldRow && !isRowPersisted(oldRow)) {
      emit('save-row', { row: oldRow, rowIndex: oldRowIndex })
    }
  }

  // Remove linhas vazias não editadas (exceto a linha de destino, se for recém-adicionada)
  if (selectedCell.value && (selectedCell.value.rowIndex !== rowIndex || selectedCell.value.colIndex !== colIndex)) {
    removeEmptyRows(rowIndex)
  }

  selectedCell.value = { rowIndex, colIndex }

  nextTick(() => {
    if (gridContainer.value) {
      gridContainer.value.focus()
    }
  })
}

function onGhostRowClick (colIndex) {
  if (props.readonly || !props.allowAddRows) return
  // Bloqueia adição enquanto houver uma linha nova não salva ("em adição") ou
  // enquanto o descarte de linha nova aguarda confirmação (modal aberto).
  if (pendingAbandon.value || hasUnpersistedNewRow()) return
  // Marca a intenção de selecionar a nova linha. A seleção é efetivada no
  // watcher de props.data.length, garantindo que a linha já exista no array.
  pendingSelectAfterAdd.value = true
  pendingColIndex.value = colIndex
  emit('add-row')
  // Fallback: caso o length não seja observado (ex: pai reutiliza o array),
  // tenta selecionar a última linha após a atualização do DOM.
  nextTick(() => {
    if (!pendingSelectAfterAdd.value) return
    const newRowIndex = props.data.length - 1
    if (newRowIndex >= 0 && props.data[newRowIndex]) {
      pendingSelectAfterAdd.value = false
      pendingColIndex.value = 0
      selectCell(newRowIndex, colIndex)
    }
  })
}

// ============================================================
// ABANDONAR LINHA NOVA (navegação para cima com confirmação)
// ============================================================
// Linha nova, não persistida e COM conteúdo (digna de confirmação antes de descartar).
function isRowAbandonable (row) {
  return !!row && !isRowPersisted(row) && !isRowBlank(row)
}

// Navegação "para cima": se a linha atual for nova e com dados, delega a decisão
// ao pai (que exibe o ConfirmationModal) em vez de descartar silenciosamente.
function navigateUp (rowIndex, colIndex, targetRowIndex, targetColIndex) {
  const row = props.data[rowIndex]
  if (isRowAbandonable(row)) {
    pendingAbandon.value = { row, rowIndex, target: { rowIndex: targetRowIndex, colIndex: targetColIndex } }
    emit('request-abandon-new-row', { row, rowIndex })
    return
  }
  selectCell(targetRowIndex, targetColIndex)
}

// Confirmado pelo usuário: descarta a linha nova e segue para o alvo.
function confirmAbandonRow () {
  const p = pendingAbandon.value
  pendingAbandon.value = null
  if (!p) return
  if (p.row._tempId) newEmptyRows.value.delete(p.row._tempId)
  emit('delete-row', { row: p.row, rowIndex: p.rowIndex, explicit: true })
  nextTick(() => {
    const target = Math.min(p.target.rowIndex, props.data.length - 1)
    if (target >= 0) selectCell(target, p.target.colIndex)
  })
}

// Cancelado pelo usuário: mantém a linha e devolve o foco.
function cancelAbandonRow () {
  pendingAbandon.value = null
  nextTick(() => {
    if (gridContainer.value) gridContainer.value.focus()
  })
}

function onGridBlur (event) {
  resetAutocomplete()
  // Emite save-row ao sair da linha nova (perda de foco)
  if (selectedCell.value) {
    const row = props.data[selectedCell.value.rowIndex]
    if (row && !isRowPersisted(row)) {
      emit('save-row', { row, rowIndex: selectedCell.value.rowIndex })
    }
  }

  // Remove linhas vazias não editadas
  if (!event.relatedTarget || !gridContainer.value?.contains(event.relatedTarget)) {
    removeEmptyRows()
  }
}

// ============================================================
// CELL CLICK & DBLCLICK
// ============================================================
function onCellClick (rowIndex, colIndex) {
  if (editingCell.value) return
  selectCell(rowIndex, colIndex)
}

function onCellDblClick (row, rowIndex, col, colIndex) {
  if (props.readonly) return
  selectCell(rowIndex, colIndex)
  if (col.type === 'lookup') {
    emitCellLookup(row, rowIndex, col, colIndex)
    return
  }
  startEditing(row, rowIndex, col, colIndex)
}

// ============================================================
// KEYBOARD NAVIGATION — usa colunasRenderizadas
// ============================================================
function onGridKeydown (event) {
  const target = event.target
  if (!gridContainer.value?.contains(target)) return

  // If editing, only handle Escape here; other keys handled by onInputKeydown
  if (editingCell.value) {
    if (event.key === 'Escape') {
      event.preventDefault()
      cancelEdit()
    }
    return
  }

  // Estado "em adição": enquanto o descarte de uma linha nova com dados aguarda
  // confirmação (modal aberto), bloqueia navegação e novas adições.
  if (pendingAbandon.value) {
    const bloqueadas = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter']
    if (bloqueadas.includes(event.key)) {
      event.preventDefault()
      return
    }
  }

  // Get current selected cell position
  const rowIndex = selectedCell.value?.rowIndex ?? 0
  const colIndex = selectedCell.value?.colIndex ?? 0
  const colunas = colunasRenderizadas.value

  const key = event.key
  const col = colunas[colIndex]
  const row = props.data[rowIndex]

  // Lookup: Enter abre busca
  if (col?.type === 'lookup' && (key === 'Enter' || key === 'F2')) {
    event.preventDefault()
    emitCellLookup(row, rowIndex, col, colIndex)
    return
  }

  switch (key) {
    case 'ArrowUp':
      event.preventDefault()
      if (rowIndex > 0) navigateUp(rowIndex, colIndex, rowIndex - 1, colIndex)
      break
    case 'ArrowDown':
      event.preventDefault()
      if (rowIndex < props.data.length - 1) {
        selectCell(rowIndex + 1, colIndex)
      } else if (props.allowAddRows && !props.readonly) {
        if (pendingAbandon.value || hasUnpersistedNewRow()) return
        emit('add-row')
        nextTick(() => {
          const newRowIndex = props.data.length - 1
          if (newRowIndex >= 0) {
            selectCell(newRowIndex, colIndex)
          }
        })
      }
      break
    case 'ArrowLeft':
      event.preventDefault()
      if (colIndex > 0) selectCell(rowIndex, colIndex - 1)
      break
    case 'ArrowRight':
      event.preventDefault()
      if (colIndex < colunas.length - 1) selectCell(rowIndex, colIndex + 1)
      break
    case 'F2':
      event.preventDefault()
      {
        const row = props.data[rowIndex]
        const col = colunas[colIndex]
        startEditing(row, rowIndex, col, colIndex)
      }
      break
    case 'Enter':
      event.preventDefault()
      // Enter em navegação: move para baixo (equivalente à seta para baixo)
      if (rowIndex < props.data.length - 1) selectCell(rowIndex + 1, colIndex)
      break
    case 'Tab':
      event.preventDefault()
      {
        const nextCol = colIndex + (event.shiftKey ? -1 : 1)
        if (nextCol < 0) {
          if (rowIndex > 0) navigateUp(rowIndex, colIndex, rowIndex - 1, colunas.length - 1)
        } else if (nextCol >= colunas.length) {
          if (rowIndex < props.data.length - 1) {
            selectCell(rowIndex + 1, 0)
          } else if (props.allowAddRows && !props.readonly) {
            // Tab na última linha com allowAddRows: adiciona linha
            if (pendingAbandon.value || hasUnpersistedNewRow()) return
            emit('add-row')
            nextTick(() => {
              const newRowIndex = props.data.length - 1
              if (newRowIndex >= 0) {
                selectCell(newRowIndex, 0)
              }
            })
          }
        } else {
          selectCell(rowIndex, nextCol)
        }
      }
      break
    default:
      // Capturar teclas imprimíveis para iniciar edição por digitação
      if (key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        event.preventDefault()
        const row = props.data[rowIndex]
        const col = colunas[colIndex]
        startEditing(row, rowIndex, col, colIndex, key)
      }
      break
  }
}

// ============================================================
// DEFINE EXPOSE
// ============================================================
defineExpose({
  setCellValue (rowIndex, field, value, displayField, displayValue) {
    const newData = [...props.data]
    if (displayField && displayValue !== undefined) {
      newData[rowIndex] = { ...newData[rowIndex], [displayField]: displayValue }
    }
    newData[rowIndex] = { ...newData[rowIndex], [field]: value }
    emit('update:data', newData)
  },
  focusCell (rowIndex, colIndex) {
    if (rowIndex >= 0 && rowIndex < props.data.length &&
        colIndex >= 0 && colIndex < colunasRenderizadas.value.length) {
      selectCell(rowIndex, colIndex)
    }
  },
  // Seleciona/foca a célula a partir do field da coluna (usado para devolver o
  // foco à célula lookup que abriu o modal, continuando a navegação de teclado).
  focusCellByField (rowIndex, field) {
    if (rowIndex == null || rowIndex < 0 || rowIndex >= props.data.length) return
    const colIndex = colunasRenderizadas.value.findIndex(c => c.field === field)
    if (colIndex >= 0) {
      selectCell(rowIndex, colIndex)
    }
  },
  getRowData (rowIndex) {
    return props.data[rowIndex]
  },
  setColumnVisible,
  confirmAbandonRow,
  cancelAbandonRow,
  refreshRow (rowIndex) {
    if (rowIndex >= 0 && rowIndex < props.data.length) {
      const newData = [...props.data]
      newData[rowIndex] = { ...newData[rowIndex] }
      emit('update:data', newData)
    }
  }
})

function onInputKeydown (event, row, rowIndex, col, colIndex) {
  if (!editingCell.value) return

  switch (event.key) {
    // Setas NÃO são interceptadas durante a edição: o navegador move o cursor
    // de texto dentro do input. A navegação entre células é tratada em
    // onGridKeydown, que só roda quando NÃO há célula em edição.
    case 'Enter':
      event.preventDefault()
      confirmEdit(row, rowIndex, col, colIndex)
      // Enter em edição: salva, sai da edição e permanece na mesma célula
      selectCell(rowIndex, colIndex)
      break
    case 'Tab':
      event.preventDefault()
      confirmEdit(row, rowIndex, col, colIndex)
      {
        const colunas = colunasRenderizadas.value
        const offset = event.shiftKey ? -1 : 1
        const nextCol = colIndex + offset
        if (nextCol < 0) {
          if (rowIndex > 0) {
            selectCell(rowIndex - 1, colunas.length - 1)
            const prevRow = props.data[rowIndex - 1]
            const prevColDef = colunas[colunas.length - 1]
            if (prevColDef && isCellEditable(prevColDef)) {
              startEditing(prevRow, rowIndex - 1, prevColDef, colunas.length - 1)
            }
          }
        } else if (nextCol >= colunas.length) {
          if (rowIndex < props.data.length - 1) {
            selectCell(rowIndex + 1, 0)
            const nextRow = props.data[rowIndex + 1]
            const firstCol = colunas[0]
            if (firstCol && isCellEditable(firstCol)) {
              startEditing(nextRow, rowIndex + 1, firstCol, 0)
            }
          }
        } else {
          selectCell(rowIndex, nextCol)
          const nextColDef = colunas[nextCol]
          if (nextColDef && isCellEditable(nextColDef)) {
            startEditing(row, rowIndex, nextColDef, nextCol)
          }
        }
      }
      break
    case 'Escape':
      event.preventDefault()
      cancelEdit()
      break
  }
}

// ============================================================
// LIFECYCLE
// ============================================================
onMounted(async () => {
  await carregarLayout()

  // Detector de overflow horizontal (thead sticky × scroll-x)
  nextTick(() => {
    checarOverflowX()
    const wrap = tableWrapRef.value
    if (wrap && typeof ResizeObserver !== 'undefined') {
      _overflowObserver = new ResizeObserver(() => checarOverflowX())
      _overflowObserver.observe(wrap)
    }
  })
})
</script>

<style scoped>
.excel-grid-wrapper {
  position: relative;
  outline: none;
  min-height: 100px;
}

.excel-grid-wrapper:focus {
  outline: none;
}

.excel-grid-loading,
.excel-grid-empty {
  padding: 1.5rem;
  text-align: center;
  color: var(--bs-secondary-color);
}

.excel-grid-table-wrap {
  /*
   * GUARDIÃO: sem overflow no base (desktop) para o thead sticky referenciar a
   * página e congelar abaixo do header do PaginaAdministrar. A classe
   * .table-overflows-x — aplicada por ResizeObserver — reativa o scroll
   * horizontal apenas quando a tabela excede a largura do wrapper.
   */
}
.excel-grid-table-wrap.table-overflows-x {
  overflow-x: auto;
}
/* No mobile o scroll horizontal permanece sempre ativo */
@media (max-width: 991.98px) {
  .excel-grid-table-wrap { overflow-x: auto; }
}

.excel-grid-table {
  width: 100%;
  /*
   * GUARDIÃO: `separate` (em vez de `collapse`) é necessário para o
   * `thead th` sticky abaixo do header do PaginaAdministrar — com `collapse`
   * o Chrome desloca a linha do cabeçalho e some com as bordas verticais.
   */
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.excel-grid-table thead th {
  position: sticky;
  /* Congela abaixo do header do PaginaAdministrar (0 fora dele, ex.: em forms) */
  top: var(--ct-admin-header-h, 0px);
  background: var(--bs-light-bg-subtle);
  border-bottom: 2px solid var(--bs-border-color);
  border-right: 1px solid var(--bs-border-color);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: var(--bs-secondary-color);
  padding: 0.5rem 0.5rem;
  white-space: nowrap;
  vertical-align: top;
  z-index: 1;
}

/* Última coluna do header sem divisor à direita */
.excel-grid-table thead th:last-child {
  border-right: none;
}

.excel-grid-cell {
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid var(--bs-border-color);
  border: 1px solid transparent;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid var(--bs-border-color);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  vertical-align: top;
}

.excel-grid-cell:hover {
  border-color: var(--bs-border-color);
}

/* Selected state (navigation) */
.excel-grid-cell-selected {
  border-color: var(--bs-primary) !important;
  border-bottom: 2px solid var(--bs-primary) !important;
  background: rgba(var(--bs-primary-rgb), 0.08) !important;
  box-shadow: 0 0 0 1px var(--bs-primary);
  position: relative;
  z-index: 3;
}

/* Editing state — a borda amarela é a própria borda da célula.
   Como o input preenche a célula (padding 0), sua borda faz o papel da
   borda amarela da célula e não sofre com o border-collapse da tabela. */
.excel-grid-cell-editing {
  border-color: var(--bs-warning) !important;
  background: var(--bs-body-bg);
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.35);
  position: relative;
  z-index: 5;
  padding: 0;
}

.excel-grid-cell-editing .excel-grid-input {
  border: 2px solid var(--bs-warning);
  border-radius: 0;
}

/* Read-only state — NOT applied to actions columns */
.excel-grid-cell-readonly {
  cursor: not-allowed;
  opacity: 0.65;
  background: var(--bs-secondary-bg);
}

/* Actions cell */
.excel-grid-cell-actions {
  cursor: default;
  opacity: 1;
  background: transparent;
  white-space: nowrap;
}

/* Container dos botões de ação: uma única linha, centralizado */
.excel-grid-cell-actions-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 2px;
}

/* Botões de ação compactos dentro do grid (sobrepõe .btn-icon global) */
.excel-grid-cell-actions-inner :deep(.btn-icon) {
  padding: 0 0.15rem;
  font-size: 0.9rem;
  line-height: 1.5rem;
  vertical-align: middle;
}

/* Hidden column style */
.excel-grid-cell-hidden {
  display: none;
}

.excel-grid-cell-text {
  display: block;
  min-height: 1.5rem;
  line-height: 1.5rem;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Conteúdo + ícone na mesma linha (flex) */
.excel-grid-cell-inner {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.excel-grid-cell-inner .excel-grid-cell-text {
  flex: 1 1 auto;
  min-width: 0;
}

/* Ícone de busca: sempre visível em célula vazia;
   em célula com conteúdo, só aparece quando a célula está selecionada/editando */
.excel-grid-cell-has-content:not(.excel-grid-cell-selected):not(.excel-grid-cell-editing) .excel-grid-lookup-btn {
  display: none;
}

/* Lookup button (search icon inside lookup cells) */
.excel-grid-lookup-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--bs-primary);
  padding: 0 2px;
  margin-left: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  vertical-align: middle;
  border-radius: 2px;
  transition: all 0.15s ease;
}

.excel-grid-lookup-btn:hover {
  background: rgba(var(--bs-primary-rgb), 0.1);
  color: var(--bs-primary-text-emphasis);
}

/* Dropdown de autocomplete */
.excel-grid-autocomplete-dropdown {
  max-height: 280px;
  overflow-y: auto;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.excel-grid-autocomplete-item {
  cursor: pointer;
  padding: 6px 10px;
  border-bottom: 1px solid var(--bs-border-color-translucent);
  transition: background 0.1s ease;
}

.excel-grid-autocomplete-item:last-child {
  border-bottom: none;
}

.excel-grid-autocomplete-item.is-hover,
.excel-grid-autocomplete-item:hover {
  background: var(--bs-primary-bg-subtle);
  box-shadow: inset 3px 0 0 0 var(--bs-primary);
}

.excel-grid-autocomplete-item.is-disabled {
  background: var(--bs-danger-bg-subtle);
  color: var(--bs-danger-text-emphasis);
  box-shadow: none;
  cursor: not-allowed;
}

/* Editor de data: input de texto mascarado + botão de calendário nativo */
.excel-grid-date-editor {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
}

.excel-grid-date-editor .excel-grid-input {
  flex: 1 1 auto;
  min-width: 0;
}

.excel-grid-date-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--bs-secondary-color);
  cursor: pointer;
}

.excel-grid-date-btn:hover {
  color: var(--bs-primary);
}

.excel-grid-date-native {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.excel-grid-autocomplete-primary {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  white-space: nowrap;
}

.excel-grid-autocomplete-primary .bi-dot {
  font-size: 0.6rem;
  color: var(--bs-secondary-color);
}

.excel-grid-autocomplete-secondary {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin-top: 2px;
  white-space: nowrap;
}

.excel-grid-autocomplete-tag {
  display: inline-block;
  font-size: 0.72rem;
  padding: 1px 8px;
  background: var(--bs-tertiary-bg);
  border-radius: 10px;
  color: var(--bs-secondary-color);
}

.excel-grid-autocomplete-empty {
  padding: 10px;
  font-size: 0.82rem;
  color: var(--bs-secondary-color);
  text-align: center;
}

/* Input de edição: ocupa toda a célula, sem borda própria
   (a borda amarela vem da célula em edição) */
.excel-grid-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 2rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-family: inherit;
  color: var(--bs-body-color);
  background: var(--bs-body-bg);
  border: none;
  border-radius: 0;
  outline: none;
}

.excel-grid-input:focus {
  outline: none;
}

/* Células alinhadas à direita (números/datas): input também à direita */
.excel-grid-cell.text-right .excel-grid-input {
  text-align: right;
}

.excel-grid-select {
  cursor: pointer;
  min-height: 2rem;
}

.font-mono {
  font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
}

.text-right {
  text-align: right;
}

/* Selected row highlight */
:deep(.excel-grid-row-selected) {
  background: rgba(var(--bs-primary-rgb), 0.03);
}

/* Ghost row for adding new rows */
.excel-grid-ghost-row {
  border-top: 2px dashed var(--bs-border-color);
}

.excel-grid-ghost-row:hover {
  background: rgba(var(--bs-primary-rgb), 0.03);
}

/* Ghost row inline (quando grid vazio) */
.excel-grid-ghost-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  margin-top: 8px;
  border: 2px dashed var(--bs-border-color);
  border-radius: 8px;
  cursor: pointer;
  color: var(--bs-secondary-color);
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.excel-grid-ghost-inline:hover {
  border-color: var(--bs-primary);
  color: var(--bs-primary);
  background: rgba(var(--bs-primary-rgb), 0.03);
}

.excel-grid-ghost-cell {
  cursor: pointer !important;
  color: var(--bs-secondary-color);
  font-size: 0.8rem;
  padding: 0.35rem 0.5rem;
}

.excel-grid-ghost-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  line-height: 18px;
  text-align: center;
  border: 1px dashed var(--bs-secondary-color);
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 700;
  margin-right: 6px;
  color: var(--bs-secondary-color);
}

.excel-grid-ghost-text {
  vertical-align: middle;
}

/* Row class for preenchimento concluido */
:deep(.row-preenchimento-concluido) {
  background: var(--bs-success-bg-subtle);
}

:deep(.row-preenchimento-concluido:hover) {
  background: var(--bs-success-bg-subtle);
}

:deep(.row-preenchimento-concluido .excel-grid-cell) {
  background: transparent;
}

/* Required mark in header */
.excel-grid-required-mark {
  color: var(--bs-danger);
  margin-left: 2px;
  font-weight: 700;
}

/* Required cell validation */
.excel-grid-cell-required {
  border-color: var(--bs-danger) !important;
  border-style: dashed;
  background: rgba(var(--bs-danger-rgb), 0.03);
}

/* Help button bar — above the table header */
.excel-grid-help-bar {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  padding: 2px 4px;
  background: var(--bs-light-bg-subtle);
  border-bottom: 1px solid var(--bs-border-color);
}

.excel-grid-cancel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  background: transparent;
  color: var(--bs-danger);
  font-size: 0.7rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  padding: 0;
  line-height: 1;
}

.excel-grid-cancel-btn:hover {
  background: var(--bs-danger-bg-subtle);
  color: var(--bs-danger-text-emphasis);
}

.excel-grid-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  background: transparent;
  color: var(--bs-danger);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  padding: 0;
  line-height: 1;
}

.excel-grid-delete-btn:hover:not(:disabled) {
  background: var(--bs-danger-bg-subtle);
  color: var(--bs-danger-text-emphasis);
  border-color: var(--bs-danger);
}

.excel-grid-delete-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.excel-grid-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 4px;
  background: var(--bs-primary);
  color: #fff;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  padding: 0;
  line-height: 1;
}

.excel-grid-help-btn:hover {
  background: var(--bs-primary-text-emphasis);
  transform: scale(1.1);
}

.excel-grid-help-btn:active {
  transform: scale(0.95);
}

.excel-grid-help-note {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  line-height: 1.4;
  padding: 0 0.25rem;
}

/* Help panel */
.excel-grid-help-panel {
  position: absolute;
  top: 32px;
  right: 4px;
  z-index: 20;
  width: 320px;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.excel-grid-help-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--bs-primary);
  color: #fff;
  font-size: 0.85rem;
}

.excel-grid-help-body {
  padding: 0.75rem;
  font-size: 0.85rem;
}

.excel-grid-help-table {
  width: 100%;
  border-collapse: collapse;
}

.excel-grid-help-table td {
  padding: 0.25rem 0.25rem;
  vertical-align: middle;
}

.excel-grid-help-key {
  white-space: nowrap;
  padding-right: 0.5rem;
}

.excel-grid-help-key kbd {
  display: inline-block;
  padding: 1px 5px;
  font-size: 0.75rem;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-weight: 600;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-bottom-width: 2px;
  border-radius: 4px;
  color: var(--bs-body-color);
  box-shadow: 0 1px 0 rgba(0,0,0,0.08);
}

.excel-grid-help-legend {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.excel-grid-help-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 2px;
  vertical-align: middle;
  margin-right: 4px;
}

.excel-grid-help-swatch-selected {
  background: rgba(var(--bs-primary-rgb), 0.05);
  border: 1px solid var(--bs-primary);
}

.excel-grid-help-swatch-editing {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-warning);
  box-shadow: 0 0 0 1px rgba(255, 193, 7, 0.25);
}

.excel-grid-help-swatch-required {
  background: rgba(var(--bs-danger-rgb), 0.03);
  border: 1px dashed var(--bs-danger);
}

.excel-grid-help-swatch-readonly {
  background: var(--bs-secondary-bg);
  border: 1px solid var(--bs-border-color);
}

/* ============================================================ */
/* ROW PENDING SAVE (linha nova ainda nao persistida) */
/* ============================================================ */
.row-pending-save {
  border-left: 3px solid var(--bs-warning) !important;
  background: rgba(var(--bs-warning-rgb), 0.05) !important;
}

.row-pending-save:hover {
  background: rgba(var(--bs-warning-rgb), 0.08) !important;
}

@keyframes pendingBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.row-pending-save td:first-child .excel-grid-cell-text {
  animation: pendingBlink 1.5s infinite;
}

.excel-grid-pending-save-marker {
  display: none;
}

/* ============================================================ */
/* COLUMN SELECTOR (gear button + popover) */
/* ============================================================ */
.excel-grid-column-selector {
  position: absolute;
  top: 2px;
  right: 36px;
  z-index: 15;
}

.excel-grid-gear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  background: transparent;
  color: var(--bs-secondary-color);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  padding: 0;
  line-height: 1;
}

.excel-grid-gear-btn:hover {
  background: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
}

.excel-grid-column-popover {
  position: absolute;
  top: 32px;
  right: 0;
  width: 520px;
  max-height: 400px;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.excel-grid-column-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--bs-primary);
  color: #fff;
  font-size: 0.85rem;
}

.excel-grid-column-popover-body {
  padding: 0.75rem;
  overflow-y: auto;
  flex: 1;
}

.excel-grid-column-search {
  margin-bottom: 0.75rem;
}

.excel-grid-column-dualpane {
  display: flex;
  gap: 8px;
}

.excel-grid-column-pane {
  flex: 1;
  min-height: 150px;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.375rem;
  overflow: hidden;
}

.excel-grid-column-pane-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0.5rem;
  background: var(--bs-tertiary-bg);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bs-secondary-color);
  border-bottom: 1px solid var(--bs-border-color);
}

.excel-grid-column-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: var(--bs-primary);
  font-size: 0.7rem;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s ease;
  padding: 0;
}

.excel-grid-column-link-btn:hover {
  background: rgba(var(--bs-primary-rgb), 0.1);
}

.excel-grid-column-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  cursor: grab;
  transition: background 0.1s ease;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.excel-grid-column-item:last-child {
  border-bottom: none;
}

.excel-grid-column-item:hover {
  background: var(--bs-tertiary-bg);
}

.excel-grid-column-item.drag-over {
  background: rgba(var(--bs-primary-rgb), 0.1);
  border-top: 2px solid var(--bs-primary);
}

.excel-grid-column-item-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.excel-grid-column-item-lock {
  color: var(--bs-secondary-color);
  font-size: 0.7rem;
}

.excel-grid-column-item-arrows {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.excel-grid-column-arrow-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 12px;
  border: none;
  background: transparent;
  color: var(--bs-secondary-color);
  font-size: 0.55rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  border-radius: 2px;
  transition: background 0.1s ease;
}

.excel-grid-column-arrow-btn:hover {
  background: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}

.excel-grid-column-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 2px;
}

.excel-grid-column-popover-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid var(--bs-border-color);
  margin-top: 0.75rem;
}

/* Fixed column indicator */
.excel-grid-column-item-fixed {
  opacity: 0.7;
}

.excel-grid-column-item-fixed:hover {
  opacity: 1;
}

/* ============================================================
   RESIZE HANDLE
   ============================================================ */
.resize-handle {
  position: absolute;
  top: 0;
  right: -1px;
  bottom: 0;
  width: 7px;
  cursor: col-resize;
  z-index: 2;
  background: transparent;
  transition: background-color 0.15s ease;
}

.resize-handle:hover,
.resize-handle:active {
  background-color: var(--bs-primary);
  opacity: 0.4;
}

.excel-grid-wrapper.is-resizing {
  user-select: none;
  -webkit-user-select: none;
}

.excel-grid-wrapper.is-resizing .resize-handle {
  opacity: 0.4;
  background-color: var(--bs-primary);
}

/* ============================================================
   LAYOUT BUTTON (toolbar)
   ============================================================ */
.excel-grid-layout-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  background: transparent;
  color: var(--bs-secondary-color);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  padding: 0;
  line-height: 1;
}

.excel-grid-layout-btn:hover {
  background: var(--bs-tertiary-bg);
  color: var(--bs-primary);
  border-color: var(--bs-primary);
}
</style>
